import i18n from 'i18next';
import { apiClient } from '@/lib/api-client';

/**
 * Encodes path segments individually while keeping forward slashes unencoded.
 * 
 * This is necessary because IIS/ASP.NET can reject or mishandle URLs with 
 * encoded forward slashes (%2F) in query strings for security reasons 
 * (path traversal prevention). URLSearchParams encodes all special characters
 * including slashes, so we must handle path encoding manually.
 * 
 * @param path - The file path to encode (e.g., "folder/subfolder/file")
 * @returns Encoded path with segments encoded but slashes preserved
 * 
 * @example
 * encodePathSegments("folder/sub folder") // "folder/sub%20folder"
 * encodePathSegments("/folder//sub/") // "folder/sub" (empty segments filtered)
 */
function encodePathSegments(path: string): string {
  if (!path) return '';
  return path
    .split('/')
    .filter(segment => segment) // Remove empty segments from '/folder/' or '//folder'
    .map(segment => encodeURIComponent(segment))
    .join('/');
}

/**
 * Builds a query string from key-value pairs.
 * 
 * Note: We don't use URLSearchParams here because it encodes forward slashes
 * as %2F, which IIS/ASP.NET can reject for security reasons. Path values
 * should be pre-encoded using encodePathSegments() before passing to this function.
 * 
 * @param params - Object with string key-value pairs
 * @returns Query string without leading '?'
 */
function buildQueryString(params: Record<string, string>): string {
  return Object.entries(params)
    .map(([key, value]) => `${encodeURIComponent(key)}=${value}`)
    .join('&');
}

// Raw item from backend API before computed properties are added
interface GridApiItem {
  Name: string;
  Path: string;
  IsFolder: boolean;
  Size?: number;
  LastModifiedString?: string;
  IsLoadedApp?: boolean;
  LoadedSubs?: number;
  IsSub?: boolean;
  IsLoadedSub?: boolean;
  Status?: 'Processing' | 'Pending' | 'Blocked' | 'Completed' | 'Failed';
  CanDelete?: boolean;
  CanRename?: boolean;
  CanDownload?: boolean;
  // App/Sub identification properties needed for Load operations
  AppId?: number;
  SubId?: number;
  SourceAppId?: string;
  SourceAppType?: string;
  SourceSubId?: string;
  SourceSubType?: string;
  Backbone?: string;
  IsNeeS?: boolean;
}

interface GridApiResponse {
  IsSuccess: boolean;
  Items?: GridApiItem[];
  ErrorMessage?: string;
}

export interface GridItem {
  // Core properties from backend API
  Name: string;
  Path: string;
  IsFolder: boolean;
  Size?: number;
  LastModifiedString?: string;

  // App/Sub specific properties from Vue app
  IsLoadedApp?: boolean;
  LoadedSubs?: number;
  IsSub?: boolean;
  IsLoadedSub?: boolean;

  // Queue status from Vue app
  Status?: 'Processing' | 'Pending' | 'Blocked' | 'Completed' | 'Failed';

  // Additional properties needed for React component compatibility
  Id: string; // Will be generated from Name/Path
  Type?: 'Folder' | 'File'; // Computed from IsFolder
  Modified?: string; // Alias for LastModifiedString

  // Operation permissions (may come from backend or be computed)
  CanDelete?: boolean;
  CanRename?: boolean;
  CanDownload?: boolean;

  // App/Sub identification properties needed for Load operations
  AppId?: number;
  SubId?: number;
  SourceAppId?: string;
  SourceAppType?: string;
  SourceSubId?: string;
  SourceSubType?: string;
  Backbone?: string;
  IsNeeS?: boolean;

  // Allow additional dynamic properties
  [key: string]: unknown;
}

class GridService {
  async getGridData(
    companyId: number,
    path: string,
    parentIsApp?: boolean
  ): Promise<GridItem[]> {
    const params: Record<string, string> = {
      companyId: encodeURIComponent(companyId.toString()),
      path: encodePathSegments(path),
    };

    if (parentIsApp !== undefined) {
      params.parentIsApp = encodeURIComponent(parentIsApp.toString());
    }

    const result = await apiClient.get<GridApiResponse>(
      `/api/Main/GetGridData?${buildQueryString(params)}`
    );

    // Handle the response structure from the backend (same as Vue app)
    if (result && result.IsSuccess && Array.isArray(result.Items)) {
      // Transform the data to include computed properties for React compatibility
      return result.Items.map((item: GridApiItem, index: number) => ({
        ...item,
        // Generate unique ID from name and path
        Id:
          item.Name + '_' + (item.Path || '').replace(/[^a-zA-Z0-9]/g, '_') + '_' + index,
        // Computed properties
        Type: item.IsFolder ? ('Folder' as const) : ('File' as const),
        Modified: item.LastModifiedString,
        // Default permissions (can be overridden by backend data)
        CanDelete: true,
        CanRename: true, // Both files and folders can be renamed
        CanDownload: true, // Both files and folders can be downloaded
      }));
    }

    // If the result has an error message, throw it (don't show snackbar here - let the hook handle it)
    if (result && !result.IsSuccess) {
      const error = new Error(result.ErrorMessage || i18n.t('load:gridService.failedToLoadGridData'));
      // Add a custom property to identify path errors - use specific known error messages
      const pathErrorMessages = [
        'Path is Invalid',
        'Provided Path is Invalid',
        'Invalid path',
        'Path not found',
      ];
      (error as Error & { isPathError?: boolean }).isPathError = 
        pathErrorMessages.some(msg => 
          result.ErrorMessage?.toLowerCase().includes(msg.toLowerCase())
        );
      throw error;
    }

    // If it's already an array, transform it directly
    if (Array.isArray(result)) {
      return (result as GridApiItem[]).map((item: GridApiItem, index: number) => ({
        ...item,
        Id:
          item.Name + '_' + (item.Path || '').replace(/[^a-zA-Z0-9]/g, '_') + '_' + index,
        Type: item.IsFolder ? ('Folder' as const) : ('File' as const),
        Modified: item.LastModifiedString,
        CanDelete: true,
        CanRename: true,
        CanDownload: true, // Both files and folders can be downloaded
      }));
    }

    // Unexpected data format - return empty array
    return [];
  }
}

export const gridService = new GridService();
